/** @type {readonly string[]} */
export const SUPPORTED_LOCALES = ['en', 'zh-CN', 'zh-TW', 'ja', 'ko', 'es', 'fr', 'de'];

export const DEFAULT_LOCALE = 'en';
export const HOME_LOCALES = ['en', 'zh-CN'];

export function localesForPage(page) {
  return page === 'home' || page === 'homepagePreview' ? HOME_LOCALES : SUPPORTED_LOCALES;
}

export function getPageLocale(page, locale) {
  if (page === 'home' || page === 'homepagePreview') return locale?.startsWith('zh') ? 'zh-CN' : 'en';
  return isSupportedLocale(locale) ? locale : DEFAULT_LOCALE;
}

/** @type {readonly string[]} */
export const PAGE_IDS = ['home', 'why', 'hardware', 'privacy', 'terms'];

/** @type {Record<string, string>} */
const PAGE_TO_SLUG = {
  home: '',
  why: 'why',
  hardware: 'hardware',
  privacy: 'privacy',
  terms: 'terms',
  homepagePrevious: 'homepage-previous',
  homepagePreview: 'homepage-preview',
};

/** @type {Record<string, string>} */
const SLUG_TO_PAGE = {
  '': 'home',
  why: 'why',
  hardware: 'hardware',
  privacy: 'privacy',
  terms: 'terms',
  'why-aquamind': 'why',
  'homepage-previous': 'homepagePrevious',
  'homepage-preview': 'homepagePreview',
};

/**
 * @param {string} locale
 * @returns {boolean}
 */
export function isSupportedLocale(locale) {
  return SUPPORTED_LOCALES.includes(locale);
}

/**
 * Parse pathname into locale and page id.
 *
 * @param {string} pathname
 * @returns {{ locale: string, page: string }}
 */
export function parseRoute(pathname) {
  const normalized = pathname.replace(/\/$/, '') || '/';
  const segments = normalized === '/' ? [] : normalized.slice(1).split('/');

  let locale = DEFAULT_LOCALE;
  let rest = segments;

  if (segments.length > 0 && isSupportedLocale(segments[0]) && segments[0] !== DEFAULT_LOCALE) {
    locale = segments[0];
    rest = segments.slice(1);
  } else if (segments.length > 0 && isSupportedLocale(segments[0]) && segments[0] === DEFAULT_LOCALE) {
    locale = DEFAULT_LOCALE;
    rest = segments.slice(1);
  }

  const slug = rest.join('/');
  const page = SLUG_TO_PAGE[slug] ?? 'home';

  return { locale: getPageLocale(page, locale), page };
}

/**
 * Build localized path for a page (no hash).
 *
 * @param {string} page
 * @param {string} [locale=DEFAULT_LOCALE]
 * @returns {string}
 */
export function pathForPage(page, locale = DEFAULT_LOCALE) {
  locale = getPageLocale(page, locale);
  const slug = PAGE_TO_SLUG[page] ?? '';

  if (locale === DEFAULT_LOCALE) {
    return slug ? `/${slug}` : '/';
  }

  return slug ? `/${locale}/${slug}` : `/${locale}`;
}

/**
 * Build localized path with optional hash fragment.
 *
 * @param {string} page
 * @param {string} locale
 * @param {string} [hash]
 * @returns {string}
 */
export function pathForPageWithHash(page, locale, hash) {
  const base = pathForPage(page, locale);

  return hash ? `${base}#${hash}` : base;
}

/**
 * Absolute URL for a localized page.
 *
 * @param {string} siteUrl
 * @param {string} page
 * @param {string} locale
 * @returns {string}
 */
export function absoluteUrlForPage(siteUrl, page, locale) {
  const origin = siteUrl.replace(/\/$/, '');
  const routePath = pathForPage(page, locale);

  return routePath === '/' ? `${origin}/` : `${origin}${routePath}`;
}

/**
 * All paths to prerender (locale x page).
 *
 * @returns {string[]}
 */
export function getAllPrerenderPaths() {
  const paths = [];

  for (const locale of SUPPORTED_LOCALES) {
    for (const page of [...PAGE_IDS, 'homepagePrevious']) {
      if (!localesForPage(page).includes(locale)) continue;
      paths.push(pathForPage(page, locale));
    }
  }

  return paths;
}

/**
 * @deprecated Use parseRoute(pathname).page
 * @param {string} pathname
 * @returns {string}
 */
export function resolvePageFromPath(pathname) {
  return parseRoute(pathname).page;
}
